import { dynamicValueDisplayText, type LeagueDynamicFieldValue } from "./dynamicFieldValue";
import type { ExternalFieldMapping } from "./externalSource";

// Shared sort/filter primitives over externally-mapped dynamic values, used by
// the external-records browser and the sort-and-filter pylon.

export type RecordSortDirection = "asc" | "desc";

export type MappedFieldOption = Pick<ExternalFieldMapping, "targetFieldID" | "targetLabel" | "targetType">;

/** Deduped field options (by targetFieldID, mapping order preserved) for sort/filter selects. */
export function mappedFieldOptions(mappings: ExternalFieldMapping[]): MappedFieldOption[] {
  const seen = new Set<string>();
  const fields: MappedFieldOption[] = [];
  for (const mapping of mappings) {
    if (seen.has(mapping.targetFieldID)) continue;
    seen.add(mapping.targetFieldID);
    fields.push({
      targetFieldID: mapping.targetFieldID,
      targetLabel: mapping.targetLabel,
      targetType: mapping.targetType,
    });
  }
  return fields;
}

/**
 * Comparable number from timing-feed text: plain numbers ("10.00", "1"),
 * clock/durations ("03:43:29", "00:17:32"), signed gaps ("+02:29.46"), and
 * values with a unit suffix ("03:44 min/km" — first token wins). Colon forms
 * become seconds. Returns undefined when nothing numeric leads the string.
 */
export function parseSortableNumber(text: string): number | undefined {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  const sign = trimmed.startsWith("-") ? -1 : 1;
  const token = trimmed.replace(/^[+-]/, "").split(/\s+/)[0] ?? "";
  if (token.includes(":")) {
    const parts = token.split(":");
    if (parts.length < 2 || parts.length > 3) return undefined;
    const valid = parts.every((part, index) =>
      (index === parts.length - 1 ? /^\d+(\.\d+)?$/ : /^\d+$/).test(part),
    );
    if (!valid) return undefined;
    return sign * parts.reduce((total, part) => total * 60 + Number(part), 0);
  }
  const value = Number(token);
  return Number.isFinite(value) && token !== "" ? sign * value : undefined;
}

/** Typed int/double pass through; string kinds go through parseSortableNumber. */
export function dynamicSortNumber(value: LeagueDynamicFieldValue | undefined): number | undefined {
  if (!value) return undefined;
  if ((value.kind === "int" || value.kind === "double") && Number.isFinite(value.value)) return value.value;
  if (value.kind === "string") return parseSortableNumber(value.value);
  return undefined;
}

export function dynamicValueIsMissing(value: LeagueDynamicFieldValue | undefined): boolean {
  if (!value) return true;
  if (value.kind === "string") return value.value.trim().length === 0;
  if (value.kind === "stringList") return value.value.length === 0;
  return false;
}

export function dynamicValueSortText(value: LeagueDynamicFieldValue | undefined): string {
  if (!value) return "";
  return dynamicValueDisplayText(value).trim();
}

/**
 * Numeric-or-duration compare when both sides parse; text localeCompare
 * otherwise. Missing values ALWAYS sort last in both directions (unlike the
 * records browser, which flips them on descending) — a broadcast top-N must
 * never lead with blanks.
 */
export function compareDynamicValues(
  a: LeagueDynamicFieldValue | undefined,
  b: LeagueDynamicFieldValue | undefined,
  direction: RecordSortDirection,
): number {
  const aMissing = dynamicValueIsMissing(a);
  const bMissing = dynamicValueIsMissing(b);
  if (aMissing || bMissing) return aMissing === bMissing ? 0 : aMissing ? 1 : -1;

  const aNumber = dynamicSortNumber(a);
  const bNumber = dynamicSortNumber(b);
  const result =
    aNumber !== undefined && bNumber !== undefined
      ? aNumber - bNumber
      : dynamicValueSortText(a).localeCompare(dynamicValueSortText(b), undefined, { numeric: true, sensitivity: "base" });
  return direction === "asc" ? result : -result;
}

/** Case-insensitive trimmed exact match on display text; empty filter passes everything. */
export function matchesFilterExact(value: LeagueDynamicFieldValue | undefined, filterValue: string | undefined): boolean {
  const needle = filterValue?.trim().toLowerCase();
  if (!needle) return true;
  if (dynamicValueIsMissing(value)) return false;
  return dynamicValueSortText(value).toLowerCase() === needle;
}
